import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../ui/index.ts';
import { clock } from '../format.ts';
import { useGame } from '../GameContext.tsx';
import { useLooseT } from '../text/loose.ts';
import { eventLine } from '../text/eventText.ts';
import styles from './EventFeed.module.css';

export interface EventFeedProps {
  limit?: number;
}

/** The team's running log: what was said, what happened. */
export function EventFeed({ limit = 8 }: EventFeedProps) {
  const { t } = useTranslation('game');
  const { view, g } = useGame();
  const p = useLooseT('phrases');
  const log = useRef<HTMLDivElement>(null);

  const lines = useMemo(() => {
    const out = [];
    for (const ev of view.events) {
      const l = eventLine(ev, { g, phrases: p, players: view.players, me: view.you.id });
      if (l) out.push(l);
    }
    return out.slice(-limit);
  }, [view.events, view.players, view.you.id, g, p, limit]);

  const last = lines[lines.length - 1]?.id;
  // Keep the newest line in view inside the log only: scrolling the line into view would drag
  // the whole page down to the feed every time something happens.
  useEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [last]);

  return (
    <Panel tone="night" className={styles['feed']} title={t('feed.title')}>
      <div
        ref={log}
        className={styles['log']}
        role="log"
        aria-live="polite"
        aria-relevant="additions"
        aria-label={t('feed.title')}
        tabIndex={0}
      >
        <ul className={styles['list']}>
          {lines.map((l) => (
            <li key={l.id} className={[styles['line'], styles[l.tone]].join(' ')}>
              <span className={styles['time']}>{clock(l.atMs)}</span>
              {l.who ? <span className={styles['who']}>{l.who}</span> : null}
              <span className={styles['text']}>{l.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </Panel>
  );
}
